import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, Settings, X, Check } from 'lucide-react';

const STORAGE_KEY = 'yup_cookie_consent_v1';

const T = {
  it: {
    title: 'Rispettiamo la tua privacy',
    body: "Utilizziamo cookie e tecnologie simili per garantire il funzionamento del sito, analizzare l'utilizzo e migliorare la tua esperienza. Puoi accettare tutti i cookie, rifiutare quelli non essenziali o personalizzare le tue preferenze.",
    accept: 'Accetta tutti',
    reject: 'Rifiuta',
    prefs: 'Preferenze',
    save: 'Salva preferenze',
    close: 'Chiudi',
    necessary: 'Necessari',
    necessaryDesc: 'Essenziali per il funzionamento del sito. Sempre attivi.',
    analytics: 'Analitici',
    analyticsDesc: "Ci aiutano a capire come utilizzi il sito per migliorarlo.",
    marketing: 'Marketing',
    marketingDesc: 'Per mostrare contenuti e promozioni personalizzate.',
    prefsTitle: 'Preferenze cookie',
    linkPrivacy: 'Privacy Policy',
    linkCookie: 'Cookie Policy',
    always: 'Sempre attivi'
  },
  en: {
    title: 'We respect your privacy',
    body: 'We use cookies and similar technologies to run the site, analyse traffic and improve your experience. Accept all, reject non-essential or customise your preferences.',
    accept: 'Accept all',
    reject: 'Reject',
    prefs: 'Preferences',
    save: 'Save preferences',
    close: 'Close',
    necessary: 'Necessary',
    necessaryDesc: 'Essential for the site to work. Always on.',
    analytics: 'Analytics',
    analyticsDesc: 'Help us understand how you use the site to improve it.',
    marketing: 'Marketing',
    marketingDesc: 'Show you personalised content and promotions.',
    prefsTitle: 'Cookie preferences',
    linkPrivacy: 'Privacy Policy',
    linkCookie: 'Cookie Policy',
    always: 'Always on'
  },
  de: {
    title: 'Wir respektieren Ihre Privatsphäre',
    body: 'Wir verwenden Cookies und ähnliche Technologien, um die Seite zu betreiben, die Nutzung zu analysieren und Ihre Erfahrung zu verbessern.',
    accept: 'Alle akzeptieren', reject: 'Ablehnen', prefs: 'Einstellungen',
    save: 'Einstellungen speichern', close: 'Schließen',
    necessary: 'Notwendig', necessaryDesc: 'Wesentlich für den Betrieb. Immer aktiv.',
    analytics: 'Analyse', analyticsDesc: 'Hilft uns die Nutzung zu verstehen und zu verbessern.',
    marketing: 'Marketing', marketingDesc: 'Personalisierte Inhalte und Angebote.',
    prefsTitle: 'Cookie-Einstellungen', linkPrivacy: 'Datenschutz', linkCookie: 'Cookie-Richtlinie', always: 'Immer aktiv'
  },
  fr: {
    title: 'Nous respectons votre vie privée',
    body: 'Nous utilisons des cookies pour faire fonctionner le site, analyser son utilisation et améliorer votre expérience.',
    accept: 'Tout accepter', reject: 'Refuser', prefs: 'Préférences',
    save: 'Enregistrer', close: 'Fermer',
    necessary: 'Nécessaires', necessaryDesc: 'Essentiels au fonctionnement. Toujours actifs.',
    analytics: 'Analytiques', analyticsDesc: 'Nous aident à comprendre votre usage.',
    marketing: 'Marketing', marketingDesc: 'Contenus et promotions personnalisés.',
    prefsTitle: 'Préférences cookies', linkPrivacy: 'Politique de confidentialité', linkCookie: 'Politique cookies', always: 'Toujours actifs'
  },
  es: {
    title: 'Respetamos tu privacidad',
    body: 'Usamos cookies y tecnologías similares para operar el sitio, analizar su uso y mejorar tu experiencia.',
    accept: 'Aceptar todo', reject: 'Rechazar', prefs: 'Preferencias',
    save: 'Guardar preferencias', close: 'Cerrar',
    necessary: 'Necesarias', necessaryDesc: 'Esenciales para el funcionamiento. Siempre activas.',
    analytics: 'Analíticas', analyticsDesc: 'Nos ayudan a entender el uso del sitio.',
    marketing: 'Marketing', marketingDesc: 'Contenidos y promociones personalizados.',
    prefsTitle: 'Preferencias de cookies', linkPrivacy: 'Política de privacidad', linkCookie: 'Política de cookies', always: 'Siempre activas'
  }
};

function readConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeConsent(payload) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...payload, timestamp: new Date().toISOString(), version: 1 }));
  } catch {}
}

export default function CookieBanner({ lang = 'it' }) {
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({ necessary: true, analytics: false, marketing: false });
  const t = T[lang] || T.en;

  useEffect(() => {
    const existing = readConsent();
    if (!existing) setVisible(true);
    else setPrefs({ necessary: true, analytics: !!existing.analytics, marketing: !!existing.marketing });

    // allow re-opening via global helper
    window.__yupOpenCookiePrefs = () => { setShowPrefs(true); setVisible(true); };
    return () => { delete window.__yupOpenCookiePrefs; };
  }, []);

  const acceptAll = () => {
    writeConsent({ necessary: true, analytics: true, marketing: true, choice: 'accept_all' });
    setVisible(false); setShowPrefs(false);
  };
  const rejectAll = () => {
    writeConsent({ necessary: true, analytics: false, marketing: false, choice: 'reject_all' });
    setVisible(false); setShowPrefs(false);
  };
  const savePrefs = () => {
    writeConsent({ ...prefs, necessary: true, choice: 'custom' });
    setVisible(false); setShowPrefs(false);
  };

  if (!visible) return null;

  // Preferences modal
  if (showPrefs) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-end md:items-center justify-center bg-black/70 backdrop-blur-sm p-0 md:p-6" data-testid="cookie-prefs-modal">
        <div className="w-full md:max-w-lg bg-[#0f0f0f] border border-[color:var(--yup-primary)]/30 rounded-t-2xl md:rounded-2xl shadow-2xl overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
            <div className="flex items-center gap-2">
              <Settings size={18} className="text-[color:var(--yup-primary)]" />
              <h3 className="text-white font-semibold">{t.prefsTitle}</h3>
            </div>
            <button
              onClick={() => setShowPrefs(false)}
              aria-label={t.close}
              className="text-white/60 hover:text-white transition"
              data-testid="cookie-prefs-close"
            >
              <X size={18} />
            </button>
          </div>
          <div className="px-6 py-5 space-y-4 max-h-[60vh] overflow-y-auto">
            {/* Necessary — always on */}
            <div className="flex items-start justify-between gap-4 p-4 rounded-lg bg-white/5 border border-white/5">
              <div className="min-w-0">
                <div className="text-white font-semibold text-sm">{t.necessary}</div>
                <p className="text-white/60 text-xs mt-1">{t.necessaryDesc}</p>
              </div>
              <span className="text-[10px] uppercase tracking-widest text-[color:var(--yup-primary)]">{t.always}</span>
            </div>
            {/* Analytics */}
            <label className="flex items-start justify-between gap-4 p-4 rounded-lg bg-white/5 border border-white/5 cursor-pointer">
              <div className="min-w-0">
                <div className="text-white font-semibold text-sm">{t.analytics}</div>
                <p className="text-white/60 text-xs mt-1">{t.analyticsDesc}</p>
              </div>
              <input
                type="checkbox"
                checked={prefs.analytics}
                onChange={e => setPrefs(p => ({ ...p, analytics: e.target.checked }))}
                className="mt-1 h-5 w-5 accent-[color:var(--yup-primary)]"
                data-testid="cookie-toggle-analytics"
              />
            </label>
            {/* Marketing */}
            <label className="flex items-start justify-between gap-4 p-4 rounded-lg bg-white/5 border border-white/5 cursor-pointer">
              <div className="min-w-0">
                <div className="text-white font-semibold text-sm">{t.marketing}</div>
                <p className="text-white/60 text-xs mt-1">{t.marketingDesc}</p>
              </div>
              <input
                type="checkbox"
                checked={prefs.marketing}
                onChange={e => setPrefs(p => ({ ...p, marketing: e.target.checked }))}
                className="mt-1 h-5 w-5 accent-[color:var(--yup-primary)]"
                data-testid="cookie-toggle-marketing"
              />
            </label>
          </div>
          <div className="px-6 py-4 border-t border-white/10 flex flex-col sm:flex-row gap-2 justify-end">
            <button
              onClick={rejectAll}
              className="px-4 py-2 rounded-lg text-white/80 border border-white/15 hover:bg-white/5 transition text-sm"
              data-testid="cookie-reject-btn-modal"
            >
              {t.reject}
            </button>
            <button
              onClick={savePrefs}
              className="px-4 py-2 rounded-lg font-bold text-black inline-flex items-center gap-2 text-sm"
              style={{ background: 'linear-gradient(135deg, var(--yup-primary), #d4c599)' }}
              data-testid="cookie-save-btn"
            >
              <Check size={16} /> {t.save}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Compact banner
  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[9998] px-3 pb-3 md:px-6 md:pb-6"
      data-testid="cookie-banner"
    >
      <div className="max-w-6xl mx-auto rounded-2xl border border-[color:var(--yup-primary)]/30 bg-[#0f0f0f]/95 backdrop-blur-xl shadow-2xl overflow-hidden">
        <div className="px-5 py-5 md:px-7 md:py-6 flex flex-col md:flex-row gap-4 md:items-center">
          <div className="flex items-start gap-3 flex-1">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(188,172,138,0.15)', border: '1px solid rgba(188,172,138,0.4)' }}
            >
              <Cookie size={18} className="text-[color:var(--yup-primary)]" />
            </div>
            <div className="min-w-0">
              <h3 className="text-white font-semibold text-sm md:text-base">{t.title}</h3>
              <p className="text-white/70 text-xs md:text-sm mt-1 leading-relaxed">
                {t.body}{' '}
                <Link to="/privacy-policy" className="underline hover:text-[color:var(--yup-primary)]">{t.linkPrivacy}</Link>
                {' · '}
                <Link to="/cookie-policy" className="underline hover:text-[color:var(--yup-primary)]">{t.linkCookie}</Link>
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
            <button
              onClick={() => setShowPrefs(true)}
              className="px-4 py-2.5 rounded-lg text-white/85 border border-white/15 hover:bg-white/5 transition text-sm inline-flex items-center justify-center gap-2"
              data-testid="cookie-prefs-btn"
            >
              <Settings size={14} /> {t.prefs}
            </button>
            <button
              onClick={rejectAll}
              className="px-4 py-2.5 rounded-lg text-white/85 border border-white/15 hover:bg-white/5 transition text-sm"
              data-testid="cookie-reject-btn"
            >
              {t.reject}
            </button>
            <button
              onClick={acceptAll}
              className="px-5 py-2.5 rounded-lg font-bold text-black transition text-sm"
              style={{ background: 'linear-gradient(135deg, var(--yup-primary), #d4c599)', boxShadow: '0 8px 20px -8px rgba(188,172,138,0.5)' }}
              data-testid="cookie-accept-btn"
            >
              {t.accept}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
